import { QueryClient, QueryFunction } from "@tanstack/react-query";
// import { createClient } from '@supabase/supabase-js'; // Remove direct createClient import
import { supabase } from '@/lib/supabaseClient'; // Import the shared client
import { buildApiUrl } from '@/lib/api';
import { log } from '@/lib/logger';

// Initialize Supabase client (ensure these are the same as in use-auth.tsx or a shared config)
// const supabaseUrl = "https://ayjoeoeltdgfokqpvovb.supabase.co";
// const supabaseAnonKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImF5am9lb2VsdGRnZm9rcXB2b3ZiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDc0MTgwMjUsImV4cCI6MjA2Mjk5NDAyNX0.lnmc3qt71W_ab1D-wIeRKLe9GGUMUVFjtDnlSHkQGcE";
// const supabase = createClient(supabaseUrl, supabaseAnonKey); // Remove local client creation

// FastAPI backend has been removed, setting this to false
export const USE_FASTAPI_BACKEND = false;

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    try {
      // Try to parse as JSON for more detailed error messages
      const errorData = JSON.parse(text);
      throw new Error(`${res.status}: ${errorData.detail || text}`);
    } catch {
      // If parsing fails, just use the text
      throw new Error(`${res.status}: ${text}`);
    }
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const apiUrl = buildApiUrl(url);
  
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Get the current user data to check if it's a SuperAdmin
  // and to include the active tenant context if it is
  const { data: userData } = await supabase.auth.getUser();
  if (userData?.user) {
    // Retrieve SuperAdmin's active tenant context from localStorage
    const isSuperAdmin = window.localStorage.getItem('isSuperAdmin') === 'true';
    
    if (isSuperAdmin) {
      const activeTenantContext = window.localStorage.getItem('superAdminActiveTenantContext');
      
      if (activeTenantContext) {
        // Only add the header if there's a context
        if (activeTenantContext !== '__NULL__') {
          headers['X-Active-Tenant-Context'] = activeTenantContext;
          log.debug("Adding tenant context header", "API", { url, activeTenantContext });
        }
      }
    }
  }
  
  log.api(method, url, { isSuperAdmin: !!userData?.user && window.localStorage.getItem('isSuperAdmin') === 'true' });

  const body = data ? JSON.stringify(data) : undefined;

  const res = await fetch(apiUrl, {
    method,
    headers,
    body,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey[0] as string;
    const apiUrl = buildApiUrl(url);
    
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Get the current user data to check if it's a SuperAdmin
    // and to include the active tenant context if it is
    const { data: userData } = await supabase.auth.getUser();
    if (userData?.user) {
      // Retrieve SuperAdmin's active tenant context from localStorage
      const isSuperAdmin = window.localStorage.getItem('isSuperAdmin') === 'true';
      if (isSuperAdmin) {
        const activeTenantContext = window.localStorage.getItem('superAdminActiveTenantContext');
        if (activeTenantContext) {
          // Only add the header if there's a context
          if (activeTenantContext !== '__NULL__') {
            headers['X-Active-Tenant-Context'] = activeTenantContext;
          }
        }
      }
    }

    const res = await fetch(apiUrl, {
      headers,
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
